import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Table, Tag, Space, Button, message, Image, Modal, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { fetchParents, createParentPassword } from '../../store/slices/studentParentsSlice';
import { fetchWpInstanceStatus } from '../../store/slices/wpConfigSlice';
import { useNavigate } from 'react-router-dom';


const ensureHttps = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

const ParentsPage = () => {
  const dispatch = useDispatch();
  const selectedBranch = useSelector(state => state.branches.selectedBranch);
  const selectedPlace = useSelector(state => state.branches.selectedPlace);
  const { items, loading, pagination } = useSelector(state => state.studentParents);
  const { instance } = useSelector(state => state.wpConfig);
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailParent, setDetailParent] = useState(null);
  const [parentFilter, setParentFilter] = useState('');
  const [studentsFilter, setStudentsFilter] = useState('');

  useEffect(() => {
    if (selectedBranch?.id) {
      dispatch(fetchParents({ branchId: selectedBranch.id, placeId: selectedPlace?.id, pageIndex, pageSize }));
      dispatch(fetchWpInstanceStatus({ branchId: selectedBranch.id, placeId: selectedPlace?.id }));
    }
  }, [dispatch, selectedBranch?.id, pageIndex, pageSize]);

  const wpStatus = instance && instance.success && instance.status ? instance.status : null;
  const isConnected = wpStatus && wpStatus.status === 'connected';

  const handleCreatePassword = async (parent) => {
    // WP bağlılık kontrolü
    if (!isConnected) {
      message.error('WP bağlı değil. Lütfen önce WP bağlayın.');
      return;
    }

    try {
      await dispatch(createParentPassword({ parentId: parent.id || parent.Id, branchId: selectedBranch.id })).unwrap();
      message.success('Şifre oluşturuldu ve gönderildi');
      // Gerekirse listeyi yenile
      dispatch(fetchParents({ branchId: selectedBranch.id, placeId: selectedPlace?.id, pageIndex, pageSize }));
    } catch (err) {
      message.error(err?.message || 'İşlem başarısız');
    }
  };

  const openDetail = (parent) => {
    setDetailParent(parent);
    setDetailOpen(true);
  };

  const getColumnSearchProps = (placeholder, value, setValue) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => {
            const v = e.target.value;
            setSelectedKeys(v ? [v] : []);
          }}
          onPressEnter={() => {
            const v = selectedKeys[0] || '';
            setValue?.(v);
            confirm();
          }}
          size="middle"
          style={{ marginBottom: 8 }}
          allowClear
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              const v = selectedKeys[0] || '';
              setValue?.(v);
              confirm();
            }}
            size="small"
            icon={<SearchOutlined />}
          >
            Ara
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              setValue?.('');
              confirm();
            }}
            size="small"
          >
            Temizle
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
  });

  const columns = [
    {
      title: 'Veli',
      dataIndex: 'parent',
      key: 'parent',
      ...getColumnSearchProps('Veli ismine göre ara', parentFilter, setParentFilter),
      filteredValue: parentFilter ? [parentFilter] : null,
      onFilter: (value, record) => {
        const name = record.fullName || record.FullName || '';
        const phone = record.phoneNumber || record.PhoneNumber || '';
        const email = record.email || record.Email || '';
        const haystack = `${name} ${phone} ${email}`;
        // Türkçe locale-aware, büyük/küçük harf DUYARSIZ arama
        return haystack.toLocaleLowerCase('tr-TR').includes((value || '').toLocaleLowerCase('tr-TR'));
      },
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.fullName || record.FullName}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{record.phoneNumber || record.PhoneNumber || '-'}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{record.email || record.Email || '-'}</div>
        </div>
      )
    },
    {
      title: 'Öğrenciler',
      dataIndex: 'students',
      key: 'students',
      ...getColumnSearchProps('Öğrenci adına göre ara', studentsFilter, setStudentsFilter),
      filteredValue: studentsFilter ? [studentsFilter] : null,
      onFilter: (value, record) => {
        const list = record.students || record.Students || [];
        const names = list.map(m => m.fullName || m.FullName).filter(Boolean);
        const haystack = names.join(', ');
        return haystack.toLocaleLowerCase('tr-TR').includes((value || '').toLocaleLowerCase('tr-TR'));
      },
      render: (_, record) => {
        const list = record.students || record.Students || [];
        const names = list.map(m => m.fullName || m.FullName).filter(Boolean);
        return names.length ? names.join(', ') : '-';
      }
    },
    {
      title: 'Şifre',
      key: 'password',
      width: 120,
      render: (_, record) => {
        const pwd = record.password || record.Password;
        return pwd && pwd.trim().length > 0 ? (
          <Tag color="green">Oluşturuldu</Tag>
        ) : (
          <Tag>Yok</Tag>
        );
      }
    },
    {
      title: 'Acil/SMS', key: 'flags',
      render: (_, r) => (
        <Space>
          <Tag color={(r.isEmergency || r.IsEmergency) ? 'red' : 'default'}>Acil</Tag>
          <Tag color={(r.isSmsChecked || r.IsSmsChecked) ? 'green' : 'default'}>SMS</Tag>
        </Space>
      )
    },
    {
      title: 'İşlemler', key: 'actions', width: 220,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openDetail(record)}>Detay</Button>
          <Button 
            type="primary" 
            onClick={() => handleCreatePassword(record)}
            disabled={!isConnected}
            title={!isConnected ? 'WP bağlı değil. Lütfen önce WP bağlayın.' : ''}
          >
            Şifre Oluştur ve Gönder
          </Button>
        </Space>
      )
    }
  ];

  const paginationProps = useMemo(() => ({
    current: (pagination.index || pageIndex) + 1,
    pageSize: pagination.size || pageSize,
    total: pagination.count || items.length,
    onChange: (page, size) => { setPageIndex(page - 1); setPageSize(size); },
    showSizeChanger: true,
    showQuickJumper: true,
  }), [pagination, pageIndex, pageSize, items.length]);

  if (!selectedBranch) {
    return (
      <div style={{ padding: 16, textAlign: 'center' }}>Lütfen önce bir branş seçiniz.</div>
    );
  }

  const detailMembers = (detailParent?.students || detailParent?.Students || []).map(m => ({
    id: m.id || m.Id,
    fullName: m.fullName || m.FullName,
    birthdate: m.birthdate || m.Birthdate,
    gender: m.gender || m.Gender,
    status: m.status || m.Status,
    imageUrl: m.imageUrl || m.ImageUrl,
    identityNumber: m.identityNumber || m.IdentityNumber,
  }));

  return (
    <div style={{ padding: 16 }}>
    
      
      {/* WP Durum Bilgisi */}
      {!isConnected && (
        <Card 
          style={{ marginBottom: 16, borderColor: '#ff4d4f' }}
          bodyStyle={{ padding: '12px 16px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ 
                display: 'inline-block', 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                background: '#ff4d4f', 
                marginRight: 8 
              }} />
              <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                WP bağlı değil. Şifre oluşturma ve gönderme işlemleri yapılamaz.
              </span>
            </div>
            <Button 
              type="link" 
              size="small" 
              onClick={() => navigate('/wp')}
              style={{ padding: 0, height: 'auto' }}
            >
              WP Bağla
            </Button>
          </div>
        </Card>
      )}
      
      <Card>
        <Table
          rowKey={(r) => r.id || r.Id}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={paginationProps}
          onChange={(pg, filters) => {
            const parent = (filters.parent && filters.parent[0]) || '';
            const students = (filters.students && filters.students[0]) || '';
            setParentFilter(parent || '');
            setStudentsFilter(students || '');
          }}
        />
      </Card>

      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        title="Öğrenci Detayları"
        width={900}
      >
        {detailMembers.length === 0 ? (
          <div style={{ color: '#888' }}>Bu veliye bağlı öğrenci bulunamadı.</div>
        ) : (
          <Table
            rowKey={(r) => r.id}
            pagination={false}
            dataSource={detailMembers}
            columns={[
              {
                title: '',
                key: 'img',
                width: 60,
                render: (_, r) => r.imageUrl ? (
                  <Image src={ensureHttps(r.imageUrl)} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
                ) : null
              },
              { title: 'Ad Soyad', dataIndex: 'fullName', key: 'fullName' },
              { title: 'Doğum Tarihi', key: 'birthdate', render: (_, r) => r.birthdate ? new Date(r.birthdate).toLocaleDateString('tr-TR') : '-' },
              { title: 'Cinsiyet', dataIndex: 'gender', key: 'gender' },
              { title: 'Durum', key: 'status', render: (_, r) => (r.status ? <Tag color="green">Aktif</Tag> : <Tag color="red">Pasif</Tag>) },
            ]}
            scroll={{ x: true, y: 400 }}
          />
        )}
      </Modal>
    </div>
  );
};

export default ParentsPage;
